import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IonSearchbar, ModalController, ToastController } from '@ionic/angular';
import * as Sentry from '@sentry/browser';
import { BackendResponse } from '@tg4/http-infrastructure/dist/src';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, filter } from 'rxjs/operators';
import {
    ReportDescriptionModalComponent
} from 'src/app/reports/modals/report-description/report-description-modal.component';
import { reportDescription } from 'src/app/reports/report-descriptions';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';
import {
    GroupedAttributesListComponent
} from '../shared/components/grouped-attributes-list/grouped-attributes-list.component';
import { ProcessTypeEnum } from '../shared/interfaces/process.interface';
import { FiltersModal } from '../shared/modals/filters/filters.modal';
import {
    AttributeFilter,
    AttributeTypeEnum,
    FilterItem,
    FilterModel
} from '../shared/modals/filters/models/filter-models';
import { ImportEmployeeNumber } from './import-employee-number/import-employee-number.component';
import { ReportField } from './interfaces/report-field.interface';
import { Report, ReportDescription, ReportType } from './interfaces/report.interface';
import { ReportsService } from './reports.service';
import { GroupedAttributes } from 'src/app/shared/interfaces/grouped-attributes.interface';
import { UniqueValues } from '../shared/helper/arrayHelper';
import { JobFollowUpComponent } from '../shared/modals/job-follow-up/job-follow-up.component';
import { JobParams } from '../shared/modals/job-follow-up/models/jobs';
import { JobFollowUpService } from '../shared/modals/job-follow-up/job-follow-up.service';
import { ReportFilters } from './model/report-filters';
import { SelectCycleComponent } from '../shared/modals/select-cycle/select-cycle.component';
import { CyclePreview, CycleProcess } from '../shared/interfaces/cycle.interface';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.page.html',
    styleUrls: ['./reports.page.scss'],
})
export class ReportsPage extends BaseClass implements OnInit {
    @ViewChild('userSearch', { static: false }) userSearch: IonSearchbar;
    @ViewChild(GroupedAttributesListComponent, { static: false }) groupedAttributesListComponent!: GroupedAttributesListComponent;

    ReportType = ReportType;
    private storageKey = 'report_selected_fields';
    private _searchSubject: Subject<string> = new Subject();


    public reports: Array<Report> = [];
    public cycles: Array<CyclePreview> = [];
    public fields: Array<ReportField> = [];
    public reportDescriptions: { [key: string]: ReportDescription } = reportDescription;

    //Flags
    public showDescriptionReport: boolean;
    public filterByGroupedAttributes: boolean;
    public isEnabledReportsByEmail: boolean;

    public limitUsersToGetReport: number = 10;

    public selectedReport: Report;
    public selectedCycleId: number = 0;
    public selectedCycleProcessConfigId: number;
    public selectedCycle: CyclePreview;
    public loadingFile = false;
    public directSubordinates = false;
    public showOptionalFields = true;
    public filters: FilterModel[] = [];
    public selectedFilters: FilterItem[] = [];
    public finalFeedbackProcesses: CycleProcess[] = [];
    public genericProcesses: CycleProcess[] = [];
    public selectedGenericProcess: number;
    public users: Array<any> = [];
    public usersToGetReport: Array<any> = [];
    public userRules: Array<any> = [];
    public groupedAttributes: Array<GroupedAttributes> = [];

    public _pageSize: number = 10;

    constructor(
        protected _toastController: ToastController
        , protected _translationService: TranslationService
        , private _reportsService: ReportsService
        , private _modalController: ModalController
        , private _router: Router
        , private _jobFollowUpService: JobFollowUpService
        , private _cdr: ChangeDetectorRef
        , private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this._setSearchSubscription();
    }

    ngOnInit() {
        this.showDescriptionReport = this._configService.settings.features.showDescriptionReport;
        this.filterByGroupedAttributes = this._configService.settings.features.filterByGroupedAttributes;
        this.isEnabledReportsByEmail = this._configService.settings.features.reportsByEmail;
        this._loadReports();
        this._loadCycles();
        this._loadFields();

        this.filters = [
            new FilterModel('filters.subordinates', [new FilterItem('directSubordinates', 'filters.directSubordinates', 'true', false)], false, false),
            new FilterModel(
                'filters.userStatus',
                [new FilterItem('userStatus', 'filters.activeUsers', '1', false), new FilterItem('userStatus', 'filters.blockedUsers', '2', false)],
                false,
                false
            ),
        ];

        this.filters.push(...AttributeTypeEnum.values().map((x) => new AttributeFilter(x, [], true)));
    }

    public goBack(): void {
        this._router.navigate(['dashboard']);
    }

    public selectReport(report: Report): void {
        this.selectedReport = report;
    }

    public selectGenericProcess(cycleProcessConfigId: number): void {
        this.selectedGenericProcess = cycleProcessConfigId;
    }
    
    public selectFinalFeedbackProcess(cycleProcessConfigId: number): void {
        this.selectedCycleProcessConfigId = cycleProcessConfigId;
    }

    public changeSubordinates(directSubordinates) {
        this.directSubordinates = directSubordinates;
    }

    public exportUsersReport(): void {
        this.loadingFile = true;
        this._reportsService.downloadFile({ reportId: this.selectedReport.id }).subscribe(
            (data) => this.notify(this._translationService.getTranslation('Report.Success'), 'success'),
            (error) => {
                console.log('Error downloading the file.');
                console.log(error);
                this.notify(this._translationService.getTranslation('Report.Error'), 'danger');
                this.loadingFile = false;

                if (this._configService.settings.production) {
                    Sentry.captureException(new Error(JSON.stringify(error)));
                }
            },
            () => (this.loadingFile = false)
        );
    }


    public export(): void {
        let optionalFields = this.filterByGroupedAttributes ? [] : this.fields.filter((field) => field.selected).map((field) => field.id);

        if (this.filterByGroupedAttributes) {
            //here we get the attributes that are checked using the type of the attribute to match with the fields
            let attributeTypes = this.groupedAttributes.map(ga => ga.attributes).reduce((acc, val) => acc.concat(val), []).filter(a => a.checked).map(a => a.type);
            attributeTypes = UniqueValues(attributeTypes);

            optionalFields = this.fields.filter((field) => attributeTypes.includes(field.attributeType)).map((field) => field.id);
        }

        this.loadingFile = true;

        let attributes = this.groupedAttributes.map(ga => ga.attributes).reduce((acc, val) => acc.concat(val), []);
        let groupedAttributesId = attributes.filter(a => a.checked).map(ga => ga.id);
        const attributeIds = this.selectedFilters && this.selectedFilters.length > 0
            ? this.selectedFilters.filter((x) => x.id === 'attributeIds').map((x) => parseInt(x.value, 10))
            : groupedAttributesId;

        const subordinatesFilter = this.selectedFilters.filter((x) => x.id === 'directSubordinates');
        const directSubordinates = subordinatesFilter && subordinatesFilter.length > 0 ? subordinatesFilter[0].value === 'true' : false;

        const userStatusFilter = this.selectedFilters.filter((x) => x.id === 'userStatus');
        const userStatus = userStatusFilter && userStatusFilter.length > 0 ? (userStatusFilter[0].value ? parseInt(userStatusFilter[0].value, 10) : null) : null;

        const usersToGetReportKeys = [];
        this.usersToGetReport.forEach((user) => {
            usersToGetReportKeys.push(user.id);
        });

        if (this.selectedReport.id === ReportType.HistoricExecutiveReportWithEvaluator && usersToGetReportKeys.length === 0) {
            this.notify(this._translationService.getTranslation('report.noUserToImport'), 'danger');
            this.loadingFile = false;
            return;
        }
        
        if (this.selectedReport.id === ReportType.FinalFeedback && !this.selectedCycleProcessConfigId) {
            this.notify(this._translationService.getTranslation('report.noProcessToImport'), 'danger');
            this.loadingFile = false;
            return;
        }

        const filter: ReportFilters = {
            optionalFields: optionalFields,
            directSubordinates: directSubordinates,
            userStatus: userStatus,
            attributeIds: attributeIds,
            usersToGetReport: usersToGetReportKeys,
            language: this._translationService.getSelectedLanguage(),
            cycleId: this.selectedCycleId,
            reportId: this.selectedReport.id,
            cycleProcessConfigId: this.selectedCycleProcessConfigId
        }

        if (this.isEnabledReportsByEmail && this.isEmailCompatibleReport(filter)) {
            console.log('isEnabledReportsByEmail');
            this.retrieveReportByEmail(filter);
        } else
            this.retrieveReport(filter);
    }

    private isEmailCompatibleReport(filters: ReportFilters): boolean {
        switch (filters.reportId) {
            case ReportType.Feedback:
            case ReportType.AllUsersReport:
            case ReportType.ConsolidatedCalibrationReport:
                console.log('incompatible report');
                return false;
            default:
                return true;
        }
    }

    private retrieveReport(filter: ReportFilters) {
        this._reportsService
            .getReport(filter)
            .subscribe(
                (data) => this.notify(this._translationService.getTranslation('Report.Success'), 'success'),
                (error) => {
                    console.log('Error downloading the file.');
                    console.log(error);
                    this.notify(this._translationService.getTranslation('Report.Error'), 'danger');
                    this.loadingFile = false;

                    if (this._configService.settings.production) {
                        Sentry.captureException(new Error(JSON.stringify(error)));
                    }
                },
                () => (this.loadingFile = false)
            );
    }

    private retrieveReportByEmail(filter: ReportFilters) {
        this._reportsService
            .getReportByEmail(filter)
            .subscribe(
                (response) => {
                    const jobId = response.data;
                    this.notify(this._translationService.getTranslation('Report.SendByEmail'), 'success');
                    const selectedReport = this.selectedReport.name || "Report";
                    const reportType = ReportType[this.selectedReport.id];

                    var currentYear = new Date().getUTCFullYear();
                    const selectedCyle = this.cycles.find(x => x.cycleId == this.selectedCycleId) || this.cycles.find(x => x.year == currentYear && x.defaultCycle) || this.cycles[0];
                    const cycleName = selectedCyle ? selectedCyle.name : "Ciclo " + new Date().getUTCFullYear();
                    const cycleId = selectedCyle ? selectedCyle.cycleId : 0;

                    const jobName = `${this._translationService.getTranslation(selectedReport)} ${cycleName}`;

                    this._startPollingForJobProgress({
                        jobId: jobId,
                        type: reportType,
                        jobName: jobName,
                        cycleId: cycleId,

                    });
                },
                (error) => {
                    console.log(error);
                    this.notify(this._translationService.getTranslation('Report.Error'), 'danger');
                    this.loadingFile = false;

                    if (this._configService.settings.production) {
                        Sentry.captureException(new Error(JSON.stringify(error)));
                    }
                },
                () => (this.loadingFile = false)
            );
    }

    private _loadReports(): void {
        this._reportsService
            .getAvailableReports()
            .pipe(
                take(1),
                map<BackendResponse<any>, Report[]>((response) => response.data)
            )
            .subscribe((reports) => {
                this.reports = reports.filter((report) => !(this._configService.settings.features.reportsToHide || []).includes(report.id));
            });
    }


    private _loadCycles(): void {
        this._reportsService.getUserCycles().subscribe((response) => {
            this.cycles = response.data;
        });
    }

    private _loadFields(): void {
        this._reportsService.getOptionalHeaderFields().subscribe((response) => {
            this.fields = response.data;
            const storedFields = localStorage.getItem(this.storageKey);
            if (storedFields) {
                const store: number[] = JSON.parse(storedFields);
                this.fields.forEach((f) => (f.selected = store.indexOf(f.id) >= 0));
            } else {
                this.fields.forEach((f) => (f.selected = false));
            }
        });
    }

    private _loadAttributes(): void {
        this._reportsService
            .getCycleGroupedAttributes(this.selectedCycleId)
            .pipe(
                take(1),
                map<BackendResponse<any>, GroupedAttributes[]>((response) => response.data)
            )
            .subscribe((groupedAttributes) => {
                this.groupedAttributes = groupedAttributes;
                this.groupedAttributesListComponent.unCheckAllAttributes();
            });
    }

    public getSelectedFilters(): FilterItem[] {
        const filters: FilterItem[] = [];
        for (const filter of this.filters) {
            filters.push(...filter.items.filter((i) => i.selected));
        }
        return filters;
    }

    public async openFilters() {
        const modal = await this._modalController.create({
            component: FiltersModal,
            cssClass: 'bigger-modal',
            componentProps: {
                filters: this.filters,
            },
        });
        modal.onDidDismiss().then((result) => {
            this.selectedFilters = this.getSelectedFilters();
        });
        await modal.present();
    }

    public removeFilter(index: number) {
        this.selectedFilters[index].selected = false;
        this.selectedFilters.splice(index, 1);
    }

    public async openDescriptionReport(report: Report) {
        const reportDescription = this.reportDescriptions[report.id];

        if (reportDescription) reportDescription.title = report.name;

        const modal = await this._modalController.create({
            component: ReportDescriptionModalComponent,
            cssClass: 'bigger-modal',
            componentProps: { data: reportDescription || { title: report.name } },
        });

        await modal.present();
    }

    public updateSearch(searchTextValue: string): void {
        this._searchSubject.next(searchTextValue);
    }

    private _loadUsers(searchString: string): void {
        this._reportsService.getUsersForReport(searchString, this._pageSize).subscribe(
            (response) => {
                this.users = response.data;

                this.users.forEach((user) => {
                    var userFound = this.usersToGetReport.find((userToGetReport) => {
                        return userToGetReport.id === user.id;
                    });
                    if (userFound) user.isSelected = true;
                });
            },
            (error) => {
                if (error.status === 403) this.notify(this._translationService.getTranslation('generic.notEnoughAccess'), 'danger');
                else this.notifyError(error);
            }
        );
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((searchValue: string) => {
            this.users = [];
            if (searchValue) {
                this._loadUsers(searchValue);
            }
        });
    }

    public setUser(user: any): void {
        var job = user.attributes.find((attr) => attr.type === AttributeTypeEnum.Job.type);
        user.job = job ? job.name : '';
        if (!this.usersToGetReport.find((users) => users.id === user.id) && this.usersToGetReport.length < this.limitUsersToGetReport) {
            this.usersToGetReport.push(user);
        }
        this.users = [];
        this.userSearch.value = '';
    }

    public removeUser(user: any) {
        this.usersToGetReport.splice(
            this.usersToGetReport.findIndex((userToGetReport) => userToGetReport.id === user.id),
            1
        );
    }

    public async addCGE() {
        const modal = await this._modalController.create({
            component: ImportEmployeeNumber,
            componentProps: {
                cycleId: this.selectedCycleId,
            },
        });

        modal.onDidDismiss().then((event) => {
            if (event && event.data) {
                if (event.data[1]) {
                    this.userRules = [];
                }

                var usersRerurned = event.data[0];

                (<any[]>usersRerurned).forEach((userData) => this.setUser(userData));

                if (usersRerurned.length > this.limitUsersToGetReport) {
                    `${this.notify(this._translationService.getTranslation('report.limitUsersTarged'), 'danger')} 
					${this.limitUsersToGetReport} 
					${this.notify(this._translationService.getTranslation('generic.users').toLowerCase(), 'danger')}`;
                }
            }
        });
        return await modal.present();
    }

    public checkAttributeId(attributeId: number[]) {
        this.groupedAttributes.map(ga =>
            ga.attributes.map(a => {
                if (attributeId.includes(a.id)) {
                    a.checked = true;
                }
            })
        );
    }

    public unCheckAttributeId(attributeId: number[]) {
        this.groupedAttributes.map(ga =>
            ga.attributes.map(a => {
                if (attributeId.includes(a.id)) {
                    a.checked = false;
                }
            })
        );
    }

    public async openJobFollowUp(): Promise<void> {
        const modal = await this._modalController.create({
            component: JobFollowUpComponent,
            cssClass: 'job-follow-up',
            componentProps: {
                cycleId: this.selectedCycleId,
                pageComponentName: ReportsPage.name,
            }
        });

        modal.onDidDismiss().then(() => {
        })

        await modal.present();
    }

    public async selectCycle() {
        const modal = await this._modalController.create({
            component: SelectCycleComponent,
            cssClass: 'select-cycle-modal',
            componentProps: {
                cycles: this.cycles,
                selectedCycleId: this.selectedCycleId
            }
        });

        modal.onDidDismiss().then((response) => {
            if (response.data) {
                this.selectedCycleId = response.data;
                this.selectedCycle = this.cycles.find(c => c.cycleId === response.data);
                this._loadSelectedCycle(this.selectedCycleId);
                this._cdr.detectChanges();
            }
        })

        await modal.present();
    }

    private _startPollingForJobProgress(job: Partial<JobParams>) {
        const service$ = () => this._reportsService.getExcelJobStatus(job.jobId);
        this._jobFollowUpService.startPollingForJobProgress(service$, {
            jobId: job.jobId,
            filters: job.filters,
            cycleId: job.cycleId,
            jobName: job.jobName,
            group: job.group,
            downloaded: false,
            processId: job.processId,
            type: job.type,
            pageComponentName: ReportsPage.name
        });
    }

    private _loadSelectedCycle(cycleId: number): void {
        this.selectedCycleId = cycleId;
        this.selectedGenericProcess = undefined;
        this.selectedCycleProcessConfigId = undefined;
        const cycle = this.cycles ? this.cycles.find((x) => x.cycleId === cycleId) : undefined;
        this.finalFeedbackProcesses = cycle && cycle.processes ? cycle.processes.filter((p) => p.type === ProcessTypeEnum.FinalFeedback).sort((a, b) => a.order - b.order) : [];
        this.genericProcesses = cycle && cycle.processes ? cycle.processes.filter((p) => p.type === ProcessTypeEnum.GenericProcess).sort((a, b) => a.order - b.order) : [];
        if (this.filterByGroupedAttributes) {
            this._loadAttributes();
        }
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }
}
